import {
    AccessLevel,
    ICorporationResponse,
    ICurrentUserResponse,
    ILocationResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { useAccess } from 'contexts/AccessContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { useMemo } from 'react';
import { useHistory } from 'react-router';
import {
    determineCorporationNavigationRoute,
    determineGlobalNavigationRoute,
    determineLocationNavigationRoute,
    getCorporationLandingPageRoute,
    getHardwareRoute,
} from 'utils/RouteHelper';

export const useScopeNavigation = (user: ICurrentUserResponse | undefined) => {
    const history = useHistory();
    const permissions = usePermissions();
    const { canViewCorporationMap } = permissions;
    const { isPremium } = useAccess();

    const corporation = useMemo(() => {
        const determineRoute = (corporation: ICorporationResponse) => {
            // If switching from a Corporation with premium access to a
            // Corporation with basic access, redirect to the new Corporation's
            // Hardware page (default page for Basic Corporations).
            if (corporation.AccessLevel === AccessLevel.Basic) {
                return getHardwareRoute(corporation.Id);
            }

            const url = determineCorporationNavigationRoute(
                history.location.pathname,
                corporation.Id,
                true,
                permissions
            );

            if (url.includes('maps') && !canViewCorporationMap) {
                return getCorporationLandingPageRoute(corporation.Id);
            }

            return url;
        };

        const navigateTo = (corporation: ICorporationResponse) => {
            history.push(determineRoute(corporation));
        };

        return { determineRoute, navigateTo };
    }, [canViewCorporationMap, history, permissions]);

    const location = useMemo(() => {
        const determineRoute = (location: ILocationResponse) => {
            return determineLocationNavigationRoute(
                history.location.pathname,
                location.Id,
                isPremium
            );
        };

        const navigateTo = (location: ILocationResponse) => {
            history.push(determineRoute(location));
        };

        return { determineRoute, navigateTo };
    }, [isPremium, history]);

    const global = useMemo(() => {
        const determineRoute = () => {
            return user
                ? determineGlobalNavigationRoute(
                      history.location.pathname,
                      user,
                      permissions
                  )
                : '/';
        };

        const navigateTo = () => {
            history.push(determineRoute());
        };

        return { determineRoute, navigateTo };
    }, [history, user, permissions]);

    return {
        determineCorporationRoute: corporation.determineRoute,
        determineLocationRoute: location.determineRoute,
        determineGlobalRoute: global.determineRoute,
        globalUrl: global.determineRoute(),
        navigateToCorporationPage: corporation.navigateTo,
        navigateToLocationPage: location.navigateTo,
        navigateToGlobalPage: global.navigateTo,
    };
};
